// hooks/finance/usePaymentRequests.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { PaymentRequest, PaymentRequestStatus, CreatePaymentRequestInput } from '@/types/database/payment-requests';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { 
  notifyApproversOfNewRequest, 
  notifyPaymentApproved, 
  notifyPaymentRejected, 
  notifyPaymentConfirmed 
} from '@/services/telegram/paymentNotificationService';
import {
  approvePaymentRequestAction,
  rejectPaymentRequestAction,
  confirmPaymentAction,
} from './usePaymentRequestActions';

export const usePaymentRequests = (status?: PaymentRequestStatus | PaymentRequestStatus[]) => {
  return useQuery({
    queryKey: ['payment-requests', status],
    queryFn: async () => {
      let query = supabase
        .from('payment_requests')
        .select(`
          *,
          department:departments(*),
          category:expense_categories(*),
          approvals:payment_request_approvals(
            id,
            approver_id,
            status,
            approved_at
          ),
          expense:expenses!payment_request_id(
            id,
            net_payment,
            total_amount
          )
        `)
        .order('created_at', { ascending: false });
      
      if (status) {
        if (Array.isArray(status)) {
          query = query.in('status', status);
        } else {
          query = query.eq('status', status);
        }
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      return data as unknown as PaymentRequest[];
    },
  });
};

export const usePaymentRequest = (id: string) => {
  return useQuery({
    queryKey: ['payment-request', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('payment_requests')
        .select(`
          *,
          department:departments(*),
          category:expense_categories(*),
          items:payment_request_items(*),
          approvals:payment_request_approvals(*),
          documents:payment_request_documents(*),
          expense:expenses!payment_request_id(
            id,
            expense_number,
            amount,
            vat_rate,
            vat_amount,
            total_amount,
            withholding_rate,
            withholding_amount,
            net_payment
          )
        `)
        .eq('id', id)
        .single();
      
      if (error) throw error;
      return data as unknown as PaymentRequest;
    },
    enabled: !!id,
  });
};

export const useCreatePaymentRequest = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: async (input: CreatePaymentRequestInput) => {
      // Generate request number
      const { data: numberData, error: numberError } = await supabase
        .rpc('generate_payment_request_number');
      
      if (numberError) throw numberError;
      
      const { items, ...requestData } = input;
      
      // Create payment request
      const { data: request, error: requestError } = await (supabase as any)
        .from('payment_requests')
        .insert({
          ...requestData,
          request_number: numberData,
          requestor_id: user?.id,
          status: 'draft',
        })
        .select()
        .single();
      
      if (requestError) throw requestError;
      
      // Create items if provided
      if (items && items.length > 0) {
        const itemsWithRequestId = items.map(item => ({
          ...item,
          payment_request_id: request.id,
        }));
        
        const { error: itemsError } = await supabase
          .from('payment_request_items')
          .insert(itemsWithRequestId);
        
        if (itemsError) throw itemsError;
      }
      
      return request as PaymentRequest;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-requests'] });
      toast.success('Payment request created successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to create payment request: ${error.message}`);
    },
  });
};

export const useSubmitPaymentRequest = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase
        .from('payment_requests')
        .update({
          status: 'pending_approval',
          submitted_at: new Date().toISOString(),
          current_approval_level: 1,
        })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      
      // Fetch complete data including items for notification
      const { data: fullRequest } = await supabase
        .from('payment_requests')
        .select(`
          *,
          department:departments(*),
          category:expense_categories(*),
          items:payment_request_items(*)
        `)
        .eq('id', id)
        .single();
      
      // Send Telegram notification to approvers
      try {
        await notifyApproversOfNewRequest(fullRequest as PaymentRequest);
      } catch (e) {
        console.error('Failed to send Telegram notification:', e);
      }
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-requests'] });
      toast.success('Payment request submitted for approval');
    },
    onError: (error: Error) => {
      toast.error(`Failed to submit payment request: ${error.message}`);
    },
  });
};

export const useApprovePaymentRequest = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, comments }: { id: string; comments?: string }) => {
      if (!user?.id) throw new Error('User is not authenticated');

      return approvePaymentRequestAction({
        id,
        userId: user.id,
        comments,
      });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['payment-requests'] }),
        queryClient.invalidateQueries({ queryKey: ['payment-request'] }),
        queryClient.invalidateQueries({ queryKey: ['bulk-payment-request'] }),
        queryClient.invalidateQueries({ queryKey: ['bulk-payment-requests'] }),
        queryClient.invalidateQueries({ queryKey: ['pending-payment-approvals'] }),
      ]);

      toast.success('Payment request approved');
    },
    onError: (error: Error) => {
      toast.error(`Failed to approve: ${error.message}`);
    },
  });
};

export const useRejectPaymentRequest = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      id,
      reason,
      comments,
    }: {
      id: string;
      reason: string;
      comments?: string;
    }) => {
      if (!user?.id) throw new Error('User is not authenticated');

      return rejectPaymentRequestAction({
        id,
        userId: user.id,
        reason,
        comments,
      });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['payment-requests'] }),
        queryClient.invalidateQueries({ queryKey: ['payment-request'] }),
        queryClient.invalidateQueries({ queryKey: ['bulk-payment-request'] }),
        queryClient.invalidateQueries({ queryKey: ['bulk-payment-requests'] }),
        queryClient.invalidateQueries({ queryKey: ['pending-payment-approvals'] }),
      ]);

      toast.success('Payment request rejected');
    },
    onError: (error: Error) => {
      toast.error(`Failed to reject: ${error.message}`);
    },
  });
};

export const useConfirmPayment = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      id,
      paymentReference,
      actualPaymentDate,
      bankAccountId,
      baseAmount,
      vatRate,
      vatAmount,
      totalExpense,
      withholdingRate,
      withholdingAmount,
      netPayment,
    }: {
      id: string;
      paymentReference?: string;
      actualPaymentDate?: string;
      bankAccountId?: string;
      baseAmount?: number;
      vatRate?: number;
      vatAmount?: number;
      totalExpense?: number;
      withholdingRate?: number;
      withholdingAmount?: number;
      netPayment?: number;
    }) => {
      if (!user?.id) throw new Error('User is not authenticated');

      return confirmPaymentAction({
        id,
        userId: user.id,
        paymentReference,
        actualPaymentDate,
        bankAccountId,
        baseAmount,
        vatRate,
        vatAmount,
        totalExpense,
        withholdingRate,
        withholdingAmount,
        netPayment,
      });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['payment-requests'] }),
        queryClient.invalidateQueries({ queryKey: ['payment-request'] }),
        queryClient.invalidateQueries({ queryKey: ['bulk-payment-request'] }),
        queryClient.invalidateQueries({ queryKey: ['bulk-payment-requests'] }),
        queryClient.invalidateQueries({ queryKey: ['expenses'] }),
        queryClient.invalidateQueries({ queryKey: ['expense-stats'] }),
      ]);

      toast.success('Payment confirmed and expense recorded');
    },
    onError: (error: Error) => {
      toast.error(`Failed to confirm payment: ${error.message}`);
    },
  });
};

export const usePendingApprovals = () => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['pending-payment-approvals', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      // Get departments where user is an approver
      const { data: approverData, error: approverError } = await supabase
        .from('department_approvers')
        .select('department_id')
        .eq('user_id', user.id)
        .eq('is_active', true);
      
      if (approverError) throw approverError;
      if (!approverData || approverData.length === 0) return [];
      
      // Get all pending requests for those departments (single-level approval)
      const departmentIds = approverData.map(a => a.department_id);
      
      const { data, error } = await supabase
        .from('payment_requests')
        .select(`
          *,
          department:departments(*),
          category:expense_categories(*)
        `)
        .in('department_id', departmentIds)
        .eq('status', 'pending_approval')
        .order('submitted_at', { ascending: true });
      
      if (error) throw error;
      return (data || []) as unknown as PaymentRequest[];
    },
    enabled: !!user?.id,
  });
};
